import React, { useState, useEffect, useRef } from 'react'
import { projectService, invoiceService, quotationService, purchaseOrderService, clientService, creditNoteService } from '../services/api'
import TableStateWrapper from '../components/TableStateWrapper'
import TablePagination from '../components/TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import { formatAmount, formatCurrency } from '../utils/currency'
import { formatDate } from '../utils/dateFormat'
import { calculatePoClaims } from '../utils/poClaims'
import useModalFeedback from '../hooks/useModalFeedback'
import useRolePermissions from '../hooks/useRolePermissions'
import TruncatedText from '../components/TruncatedText'
import ProjectPurchaseOrderCreateModal from '../components/ProjectPurchaseOrderCreateModal'
import MultiSelectFilterDropdown from '../components/MultiSelectFilterDropdown'
import CollapsibleFilters from '../components/CollapsibleFilters'

const DEFAULT_PO_ATTACHMENT_URL = new URL('../assets/default-values/No_PO.jpg', import.meta.url).href
const ALLOWED_PO_ATTACHMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'jpg', 'jpeg', 'png']

const isAllowedAttachmentFile = (file) => {
  if (!file) return false
  const extension = String(file.name || '').split('.').pop().toLowerCase()
  return ALLOWED_PO_ATTACHMENT_EXTENSIONS.includes(extension)
}

const getAttachmentDisplay = (value) => {
  const rawValue = String(value || '').trim()
  const fileName = rawValue ? rawValue.split('/').pop().split('\\').pop() : 'No_PO.jpg'
  const isDefaultFile = fileName.toLowerCase() === 'no_po.jpg'

  return { fileName, isDefaultFile }
}

export default function Projects() {
  const { showMessage, showConfirm, modalElement } = useModalFeedback()
  const { isViewer, canMutate } = useRolePermissions()
  const [projects, setProjects] = useState([])
  const [clients, setClients] = useState([])
  const [invoices, setInvoices] = useState([])
  const [invoiceModalCreditNotes, setInvoiceModalCreditNotes] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableError, setTableError] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [showQuotationModal, setShowQuotationModal] = useState(false)
  const [showPOModal, setShowPOModal] = useState(false)
  const [poModalMode, setPoModalMode] = useState('confirm')
  const [projectPurchaseOrders, setProjectPurchaseOrders] = useState([])
  const [selectedInvoicePoId, setSelectedInvoicePoId] = useState('')
  const [projectPoTotal, setProjectPoTotal] = useState(0)
  const [editingId, setEditingId] = useState(null)
  const [selectedProject, setSelectedProject] = useState(null)
  const [paymentType, setPaymentType] = useState('Full')
  const [progressPercentage, setProgressPercentage] = useState(50)
  const [claimMode, setClaimMode] = useState('percentage') // 'percentage' or 'amount'
  const [claimAmountInput, setClaimAmountInput] = useState('')
  const [quotationFormData, setQuotationFormData] = useState({
    quotation_amount: '',
    remarks: ''
  })
  const [poFormData, setPoFormData] = useState({
    po_no: '',
    po_attachment: null,
    po_attachment_name: ''
  })
  const [formData, setFormData] = useState({
    project_code: '',
    client_id: '',
    po_no: '',
    po_attachment: null,
    po_attachment_name: '',
    project_name: '',
    company_name: '',
    po_value: '',
    status: 'Proposal',
    quotation_generated: false
  })
  const [items, setItems] = useState([])
  const [currentItem, setCurrentItem] = useState({
    description: '',
    price: '',
    quantity: ''
  })
  const [projectItemsPage, setProjectItemsPage] = useState(1)
  const { containerRef: projectItemsTableRef, rowsPerPage: projectItemsRowsPerPage } = useResponsivePageSize({ reservedHeight: 640, rowHeight: 48, minRows: 3, maxRows: 6 })
  const [editingItemId, setEditingItemId] = useState(null)
  const [isViewMode, setIsViewMode] = useState(false)
  const [filters, setFilters] = useState({
    clientNames: [],
    poNumbers: [],
    projectNames: [],
    statuses: []
  })
  const [filterSearches, setFilterSearches] = useState({
    poNumber: '',
    projectName: '',
    status: ''
  })
  const [openDropdowns, setOpenDropdowns] = useState({
    poNumber: false,
    projectName: false,
    status: false
  })
  const [projectPage, setProjectPage] = useState(1)
  const { containerRef: projectTableRef, rowsPerPage: projectRowsPerPage } = useResponsivePageSize({ reservedHeight: 520, footerHeight: 24, rowHeight: 52, minRows: 5, maxRows: 14 })
  const filterContainerRef = useRef(null)

  // Fetch projects and invoices on mount
  useEffect(() => {
    fetchProjects()
    fetchInvoices()
    fetchClients()
  }, [])

  // Refresh data when window regains focus ( user returns from another tab)
  useEffect(() => {
    const handleFocus = () => {
      fetchProjects()
      fetchInvoices()
      fetchClients()
    }

    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  // Handle clicks outside filter dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterContainerRef.current && !filterContainerRef.current.contains(event.target)) {
        setOpenDropdowns({
          poNumber: false,
          projectName: false,
          status: false
        })
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchProjects = async () => {
    try {
      setLoading(true)
      setTableError(null)
      const response = await projectService.getAll()
      setProjects(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch projects:', error)
      setTableError(error.response?.data?.message || 'Failed to fetch projects')
    } finally {
      setLoading(false)
    }
  }

  const fetchInvoices = async () => {
    try {
      const response = await invoiceService.getAll()
      setInvoices(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch invoices:', error)
    }
  }

  const fetchClients = async () => {
    try {
      const response = await clientService.getAll()
      setClients(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch clients:', error)
    }
  }

  const fetchProjectPurchaseOrders = async (projectId) => {
    if (!projectId) {
      setProjectPurchaseOrders([])
      setSelectedInvoicePoId('')
      return []
    }

    try {
      const response = await purchaseOrderService.getByProject(projectId)
      const poList = response.data?.data || []
      setProjectPurchaseOrders(poList)
      setSelectedInvoicePoId(poList[0]?.po_id ? String(poList[0].po_id) : '')
      return poList
    } catch (error) {
      console.error('Failed to fetch purchase orders for project:', error)
      setProjectPurchaseOrders([])
      setSelectedInvoicePoId('')
      return []
    }
  }

  const loadProjectPoTotal = async (projectId, fallbackValue = 0) => {
    if (!projectId) {
      setProjectPoTotal(0)
      return 0
    }

    try {
      const response = await purchaseOrderService.getByProject(projectId)
      const poList = response.data?.data || []
      const total = poList.reduce((sum, po) => sum + Number(po?.po_value || 0), 0)
      const normalizedTotal = total > 0 ? total : Number(fallbackValue || 0)
      setProjectPoTotal(normalizedTotal)
      setFormData((prev) => ({
        ...prev,
        po_value: String(normalizedTotal || 0)
      }))
      return normalizedTotal
    } catch (error) {
      console.error('Failed to load purchase order total for project:', error)
      const normalizedFallback = Number(fallbackValue || 0)
      setProjectPoTotal(normalizedFallback)
      setFormData((prev) => ({
        ...prev,
        po_value: String(normalizedFallback || 0)
      }))
      return normalizedFallback
    }
  }

  const calculateTotalInvoiced = (projectId) => {
    const project = projects.find((p) => p.project_id === projectId)
    const linkedPos = Array.isArray(project?.PurchaseOrders) ? project.PurchaseOrders : []
    const projectPoIds = new Set(
      linkedPos
        .map((po) => Number(po?.po_id))
        .filter((poId) => Number.isFinite(poId))
    )
    const projectPoNumbers = new Set(
      linkedPos
        .map((po) => String(po?.po_number || '').trim())
        .filter(Boolean)
    )

    const normalizeInvoiceType = (inv) => {
      const rawType = inv?.remarks_type || inv?.remarksType || inv?.inv_type || inv?.invType || inv?.invoice_type
      const normalized = String(rawType || '').trim().toLowerCase()
      if (normalized === 'outgoing') return 'Outgoing'
      if (normalized === 'incoming') return 'Incoming'
      return ''
    }

    // Count ALL invoices for this project, regardless of Incoming/Outgoing type
    return invoices
      .filter((inv) => {
        const invoicePoId = Number(inv?.po_id)
        const invoicePoNumber = String(inv?.PurchaseOrder?.po_number || inv?.po_number || '').trim()
        if (projectPoIds.size > 0) {
          return (
            (Number.isFinite(invoicePoId) && projectPoIds.has(invoicePoId))
            || (invoicePoNumber && projectPoNumbers.has(invoicePoNumber))
          )
        }

        // Fallback for legacy projects without PO rows yet.
        const invoiceProjectId = Number(inv?.project_id)
        return invoiceProjectId === Number(projectId)
      })
      .reduce((sum, inv) => {
        const amount = Number(inv?.invoice_amount ?? inv?.progress_amount ?? 0)
        return sum + (Number.isFinite(amount) ? amount : 0)
      }, 0)
  }

  const calculateProgress = (projectId) => {
    const project = projects.find(p => p.project_id === projectId)
    const poValue = project ? calculateProjectTotalPO(project) : 0
    if (!poValue) return 0
    const totalInvoiced = calculateTotalInvoiced(projectId)
    return ((totalInvoiced / parseFloat(poValue)) * 100).toFixed(2)
  }

  const calculateBalance = (projectId) => {
    const project = projects.find(p => p.project_id === projectId)
    const poValue = project ? calculateProjectTotalPO(project) : 0
    const totalInvoiced = calculateTotalInvoiced(projectId)
    return Math.max(0, poValue - totalInvoiced)
  }

  const calculateProjectTotalPO = (project) => {
    if (!project) return 0
    const linkedPos = Array.isArray(project.PurchaseOrders) ? project.PurchaseOrders : []
    const totalPo = linkedPos.reduce((sum, po) => sum + Number(po?.po_value || 0), 0)
    if (totalPo > 0) {
      return totalPo
    }
    return Number(project.po_value || 0)
  }

  const getLinkedInvoicesForProject = (project) => {
    const linkedPos = Array.isArray(project?.PurchaseOrders) ? project.PurchaseOrders : []
    const projectId = Number(project?.project_id)

    const projectPoIds = new Set(
      linkedPos
        .map((po) => Number(po?.po_id))
        .filter((poId) => Number.isFinite(poId))
    )

    const projectPoNumbers = new Set(
      linkedPos
        .map((po) => String(po?.po_number || '').trim())
        .filter(Boolean)
    )

    return invoices.filter((inv) => {
      const invoicePoId = Number(inv?.po_id)
      const invoicePoNumber = String(inv?.PurchaseOrder?.po_number || inv?.po_number || '').trim()

      if (projectPoIds.size > 0) {
        return (
          (Number.isFinite(invoicePoId) && projectPoIds.has(invoicePoId))
          || (invoicePoNumber && projectPoNumbers.has(invoicePoNumber))
        )
      }

      return Number(inv?.project_id) === projectId
    })
  }

  const getPaymentStatus = (project) => {
    const linkedInvoices = getLinkedInvoicesForProject(project)
    if (linkedInvoices.length === 0) return 'No Invoices'

    const allPaid = linkedInvoices.every(
      (inv) => String(inv?.status || '').trim().toLowerCase() === 'paid'
    )

    return allPaid ? 'Paid' : 'Unpaid'
  }

  const generateProjectNumber = () => {
    // Generate project number format: PROJ-YYYY-MM-NNNNN
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const random = Math.floor(Math.random() * 10000).toString().padStart(5, '0')
    return `PROJ-${year}-${month}-${random}`
  }

  const generatePONumber = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const random = Math.floor(Math.random() * 100000).toString().padStart(5, '0')
    return `PO-${year}-${month}-${random}`
  }

  const getAllowedStatuses = (currentStatus) => {
    const workflow = {
      'Proposal': ['Proposal', 'Confirm'],
      'Confirm': ['Confirm', 'Ongoing'],
      'Ongoing': ['Ongoing', 'Close'],
      'Close': ['Close']
    }
    return workflow[currentStatus] || ['Proposal']
  }

  const getNewProjectStatuses = () => {
    // For new projects, only allow Proposal and Confirm
    return ['Proposal', 'Confirm']
  }

  const getUniqueClientNames = () => {
    return [...new Set(projects.map(project => String(project.company_name || '').trim()).filter(Boolean))].sort()
  }

  const getUniquePONumbers = () => {
    // Show only PO numbers for selected clients, or all POs if no client selected
    const filteredProjects = filters.clientNames.length > 0 
      ? projects.filter(p => filters.clientNames.includes(p.company_name))
      : projects
    const unique = [...new Set(filteredProjects.map(p => p.po_no || p.project_code))].filter(Boolean).sort()
    return unique.filter(code => code.toLowerCase().includes(filterSearches.poNumber.toLowerCase()))
  }

  const getUniqueProjectNames = () => {
    // Show only project names for selected clients and POs
    const filteredProjects = projects.filter(p => {
      const matchClient = filters.clientNames.length === 0 || filters.clientNames.includes(p.company_name)
      const matchPO = filters.poNumbers.length === 0 || filters.poNumbers.includes(p.po_no || p.project_code)
      return matchClient && matchPO
    })
    const unique = [...new Set(filteredProjects.map(p => p.project_name))].filter(Boolean).sort()
    return unique.filter(name => name.toLowerCase().includes(filterSearches.projectName.toLowerCase()))
  }

  const getAllStatuses = () => {
    // Show only statuses for selected clients, POs, and project names
    const filteredProjects = projects.filter(p => {
      const matchClient = filters.clientNames.length === 0 || filters.clientNames.includes(p.company_name)
      const matchPO = filters.poNumbers.length === 0 || filters.poNumbers.includes(p.po_no || p.project_code)
      const matchName = filters.projectNames.length === 0 || filters.projectNames.includes(p.project_name)
      return matchClient && matchPO && matchName
    })
    const statuses = [...new Set(filteredProjects.map(p => p.status))].filter(Boolean)
    return ['Proposal', 'Confirm', 'Ongoing', 'Close'].filter(status => 
      statuses.includes(status) && status.toLowerCase().includes(filterSearches.status.toLowerCase())
    )
  }

  const getFilteredProjects = () => {
    return projects.filter(project => {
      const matchClient = filters.clientNames.length === 0 || filters.clientNames.includes(project.company_name)
      const matchPO = filters.poNumbers.length === 0 || filters.poNumbers.includes(project.po_no || project.project_code)
      const matchName = filters.projectNames.length === 0 || filters.projectNames.includes(project.project_name)
      const matchStatus = filters.statuses.length === 0 || filters.statuses.includes(project.status)
      return matchClient && matchPO && matchName && matchStatus
    })
  }

  const filteredProjects = getFilteredProjects()
  const totalProjectPages = Math.max(1, Math.ceil(filteredProjects.length / projectRowsPerPage))
  const paginatedProjects = filteredProjects.slice((projectPage - 1) * projectRowsPerPage, projectPage * projectRowsPerPage)

  useEffect(() => {
    setProjectPage(1)
  }, [filters, projectRowsPerPage])

  useEffect(() => {
    setProjectPage((prev) => Math.min(prev, totalProjectPages))
  }, [totalProjectPages])

  const totalProjectItemPages = Math.max(1, Math.ceil(items.length / projectItemsRowsPerPage))
  const paginatedProjectItems = items.slice((projectItemsPage - 1) * projectItemsRowsPerPage, projectItemsPage * projectItemsRowsPerPage)

  useEffect(() => {
    setProjectItemsPage((prev) => Math.min(prev, totalProjectItemPages))
  }, [totalProjectItemPages])

  const handleFilterCheckChange = (type, value, checked) => {
    setFilters(prev => {
      const current = prev[type] || []
      if (checked) {
        return { ...prev, [type]: [...current, value] }
      } else {
        return { ...prev, [type]: current.filter(v => v !== value) }
      }
    })
  }

  const handleFilterSearchChange = (field, value) => {
    setFilterSearches(prev => ({ ...prev, [field]: value }))
  }

  const toggleDropdown = (field) => {
    setOpenDropdowns(prev => ({
      poNumber: field === 'poNumber' ? !prev.poNumber : false,
      projectName: field === 'projectName' ? !prev.projectName : false,
      status: field === 'status' ? !prev.status : false
    }))
  }

  const clearAllFilters = () => {
    setFilters({ clientNames: [], poNumbers: [], projectNames: [], statuses: [] })
    setFilterSearches({ poNumber: '', projectName: '', status: '' })
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name === 'client_id') {
      const selectedClient = clients.find(c => String(c.client_id) === String(value))
      setFormData(prev => ({
        ...prev,
        client_id: value,
        company_name: selectedClient?.client_name || ''
      }))
      return
    }
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleItemChange = (e) => {
    const { name, value } = e.target
    setCurrentItem(prev => ({ ...prev, [name]: value }))
  }

  const normalizeDetailItemsForPurchaseOrder = (rawItems = []) => {
    if (!Array.isArray(rawItems)) return []

    return rawItems
      .map((item) => ({
        description: String(item?.description || '').trim(),
        price: Number.parseFloat(item?.price || 0),
        quantity: Number.parseFloat(item?.quantity || 0)
      }))
      .filter((item) => item.description && Number.isFinite(item.price) && Number.isFinite(item.quantity) && item.quantity > 0)
  }

  const buildPurchaseOrderDetailItems = async ({ preferredItems = [] }) => {
    const normalizedPreferredItems = normalizeDetailItemsForPurchaseOrder(preferredItems)
    return normalizedPreferredItems
  }

  const upsertPurchaseOrderForProject = async ({ projectId, poNumber, poValue, companyName, remarks, file, remarksType = 'Incoming', detailItems = [] }) => {
    const poDate = new Date().toISOString().split('T')[0]
    const existingPOs = await purchaseOrderService.getByProject(projectId)
    const existingPO = existingPOs.data.data && existingPOs.data.data.length > 0 ? existingPOs.data.data[0] : null
    const fallbackAttachmentPath = DEFAULT_PO_ATTACHMENT_URL
    const resolvedDetailItems = await buildPurchaseOrderDetailItems({ preferredItems: detailItems })
    if (resolvedDetailItems.length === 0) {
      throw new Error('Please add at least one PO detail item before saving purchase order.')
    }

    // PO document uploads were removed — pdf_path holds the generated PDF.
    const payload = {
      po_number: poNumber,
      po_date: poDate,
      po_value: poValue || 0,
      vendor_client_details: companyName || '',
      remarks: remarks || '',
      remarks_type: remarksType || 'Incoming',
      status: 'Unpaid',
      detail_items: resolvedDetailItems
    }

    if (existingPO) {
      await purchaseOrderService.update(existingPO.po_id, payload)
    } else {
      await purchaseOrderService.create({
        project_id: projectId,
        ...payload,
        payment_term: null
      })
    }

    return fallbackAttachmentPath
  }

  const syncQuotationForProject = async ({ projectId, status, poNo, poAttachment, amount }) => {
    try {
      const quotationResponse = await quotationService.getByProject(projectId)
      const projectQuotations = quotationResponse.data.data || []
      if (projectQuotations.length === 0) {
        return
      }

      const activeQuotation = projectQuotations[0]
      const quotationUpdates = {}

      if (status !== undefined) {
        quotationUpdates.status = status
      }
      if (poNo !== undefined) {
        quotationUpdates.po_no = poNo || null
      }
      if (poAttachment !== undefined) {
        quotationUpdates.po_attachment = poAttachment || null
      }
      if (amount !== undefined && amount !== null && amount !== '') {
        quotationUpdates.amount = parseFloat(amount)
      }

      if (Object.keys(quotationUpdates).length > 0) {
        await quotationService.update(activeQuotation.quotation_id, quotationUpdates)
      }
    } catch (error) {
      // Failed to sync quotation for project
    }
  }

  const addItem = async () => {
    if (!currentItem.description || !currentItem.price || !currentItem.quantity) {
      showMessage('Please fill in all item fields', 'Validation')
      return
    }
    const amount = parseFloat(currentItem.price) * parseFloat(currentItem.quantity)
    const newItem = {
      description: currentItem.description,
      price: parseFloat(currentItem.price),
      quantity: parseFloat(currentItem.quantity),
      amount: amount
    }
    
    try {
      // If editing an existing project, save to database
      if (editingId) {
        const response = await projectService.addDetailItem(editingId, newItem)
        newItem.id = response.data.data.detail_item_id || `new-${Date.now()}`
        newItem.detail_item_id = response.data.data.detail_item_id
      } else {
        // For new projects, just use local ID
        newItem.id = `new-${Date.now()}-${Math.random()}`
      }
      
      setItems([...items, newItem])
      setCurrentItem({ description: '', price: '', quantity: '' })
      // Auto-update po_value with total of all items
      const totalAmount = items.reduce((sum, item) => sum + item.amount, 0) + amount
      setFormData(prev => ({ ...prev, po_value: totalAmount.toString() }))
    } catch (error) {
      console.error('Failed to add item:', error)
      showMessage('Failed to add item', 'Error')
    }
  }

  const removeItem = async (id) => {
    try {
      const item = items.find(i => i.id === id)
      // Delete from database if it has a detail_item_id
      if (editingId && item.detail_item_id) {
        await projectService.deleteDetailItem(editingId, item.detail_item_id)
      }
      
      const newItems = items.filter(item => item.id !== id)
      setItems(newItems)
      setEditingItemId(null)
      // Recalculate po_value
      const totalAmount = newItems.reduce((sum, item) => sum + item.amount, 0)
      setFormData(prev => ({ ...prev, po_value: totalAmount.toString() }))
    } catch (error) {
      console.error('Failed to remove item:', error)
      showMessage('Failed to remove item', 'Error')
    }
  }

  const startEditItem = (item) => {
    setEditingItemId(item.id)
    setCurrentItem({
      description: item.description,
      price: item.price.toString(),
      quantity: item.quantity.toString()
    })
  }

  const saveEditItem = async (id) => {
    if (!currentItem.description || !currentItem.price || !currentItem.quantity) {
      showMessage('Please fill in all item fields', 'Validation')
      return
    }
    const amount = parseFloat(currentItem.price) * parseFloat(currentItem.quantity)
    
    try {
      const item = items.find(i => i.id === id)
      const updatedItemData = {
        description: currentItem.description,
        price: parseFloat(currentItem.price),
        quantity: parseFloat(currentItem.quantity),
        amount: amount
      }
      
      // Save to database if it has a detail_item_id
      if (editingId && item.detail_item_id) {
        await projectService.updateDetailItem(editingId, item.detail_item_id, updatedItemData)
      }
      
      const newItems = items.map(item => 
        item.id === id 
          ? {
              ...item,
              ...updatedItemData
            }
          : item
      )
      setItems(newItems)
      setEditingItemId(null)
      setCurrentItem({ description: '', price: '', quantity: '' })
      // Recalculate po_value
      const totalAmount = newItems.reduce((sum, item) => sum + item.amount, 0)
      setFormData(prev => ({ ...prev, po_value: totalAmount.toString() }))
    } catch (error) {
      console.error('Failed to save item:', error)
      showMessage('Failed to save item', 'Error')
    }
  }

  const cancelEditItem = () => {
    setEditingItemId(null)
    setCurrentItem({ description: '', price: '', quantity: '' })
  }

  const getTotalAmount = () => {
    return items.reduce((sum, item) => sum + item.amount, 0)
  }

  const getProcessFlow = () => {
    const statuses = ['Proposal', 'Confirm', 'Ongoing', 'Close']
    const currentStatusIndex = statuses.indexOf(formData.status)
    const project = projects.find(p => p.project_id === editingId)
    const isClosedWithoutIntermediateDates =
      formData.status === 'Close' &&
      project &&
      !project.confirm_date &&
      !project.ongoing_date
    
    return statuses.map((status, index) => {
      let date = null
      if (project) {
        switch(status) {
          case 'Proposal':
            date = project.proposal_date || project.created_at
            break
          case 'Confirm':
            date = project.confirm_date
            break
          case 'Ongoing':
            date = project.ongoing_date
            break
          case 'Close':
            date = project.close_date
            break
        }
      }

      const isSkipped =
        isClosedWithoutIntermediateDates &&
        (status === 'Confirm' || status === 'Ongoing')

      const isCompleted = !isSkipped && index < currentStatusIndex
      const isCurrent = !isSkipped && index === currentStatusIndex
      const isCompletedOrCurrent = isCompleted || isCurrent
      
      let displayText = ''
      if (isSkipped) {
        displayText = '-'
      } else if (isCompletedOrCurrent) {
        displayText = date ? formatDate(date) : 'In Progress'
      } else {
        displayText = '-'
      }
      
      return {
        status: status,
        isCompleted: isCompleted,
        isCurrent: isCurrent,
        isSkipped: isSkipped,
        date: date,
        displayText: displayText
      }
    })
  }

  const openCreateModal = () => {
    setEditingId(null)
    setIsViewMode(false)
    setProjectPoTotal(0)
    setFormData({
      project_code: '',
      client_id: '',
      po_no: '',
      po_attachment: null,
      po_attachment_name: '',
      project_name: '',
      company_name: '',
      po_value: '',
      status: 'Proposal',
      po_type: 'Incoming',
      quotation_generated: false
    })
    setItems([])
    setCurrentItem({ description: '', price: '', quantity: '' })
    setEditingItemId(null)
    setShowModal(true)
  }

  const openEditModal = (project) => {
    // Can only edit if not in Close status
    if (project.status === 'Close') {
      openViewModal(project)
      return
    }
    const matchedClient = clients.find(c => c.client_id === project.client_id) || clients.find(c => c.client_name === project.company_name)
    setEditingId(project.project_id)
    setIsViewMode(false)
    setProjectPoTotal(Number(project.po_value || 0))
    setFormData({
      project_code: project.project_code,
      client_id: matchedClient?.client_id || '',
      po_no: project.po_no || '',
      po_attachment: null,
      po_attachment_name: project.po_attachment || '',
      project_name: project.project_name,
      company_name: project.company_name || '',
      po_value: project.po_value || '',
      status: project.status || 'Proposal',
      po_type: project.PurchaseOrders?.[0]?.remarks_type || 'Incoming',
      quotation_generated: project.quotation_generated || false
    })
    setItems([])
    setCurrentItem({ description: '', price: '', quantity: '' })
    setEditingItemId(null)
    loadProjectPoTotal(project.project_id, project.po_value)
    loadProjectDetailItems(project.project_id)
    setShowModal(true)
  }

  const openViewModal = (project) => {
    const matchedClient = clients.find(c => c.client_id === project.client_id) || clients.find(c => c.client_name === project.company_name)
    setEditingId(project.project_id)
    setIsViewMode(true)
    setProjectPoTotal(Number(project.po_value || 0))
    setFormData({
      project_code: project.project_code,
      client_id: matchedClient?.client_id || '',
      po_no: project.po_no || '',
      po_attachment: null,
      po_attachment_name: project.po_attachment || '',
      project_name: project.project_name,
      company_name: project.company_name || '',
      po_value: project.po_value || '',
      status: project.status || 'Proposal',
      po_type: project.PurchaseOrders?.[0]?.remarks_type || 'Incoming',
      quotation_generated: project.quotation_generated || false
    })
    setItems([])
    setCurrentItem({ description: '', price: '', quantity: '' })
    setEditingItemId(null)
    loadProjectPoTotal(project.project_id, project.po_value)
    loadProjectDetailItems(project.project_id)
    setShowModal(true)
  }

  const loadProjectDetailItems = async (projectId) => {
    try {
      const response = await projectService.getDetailItems(projectId)
      // Handle both response.data.data and response.data directly
      const detailItems = (response.data && response.data.data) ? response.data.data : (Array.isArray(response.data) ? response.data : [])
      setItems(detailItems.map(item => ({
        id: item.detail_item_id || `new-${Date.now()}-${Math.random()}`,
        description: item.description,
        price: parseFloat(item.price),
        quantity: parseFloat(item.quantity),
        amount: parseFloat(item.amount || item.price * item.quantity),
        detail_item_id: item.detail_item_id // Keep track of DB ID for updates
      })))
    } catch (error) {
      console.error('Failed to load detail items:', error)
      setItems([])
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)

      const isConfirm = formData.status === 'Confirm'
      if (!formData.client_id) {
        showMessage('Please select client from Client list', 'Validation')
        setLoading(false)
        return
      }

      const selectedClient = clients.find(c => String(c.client_id) === String(formData.client_id))
      if (!selectedClient) {
        showMessage('Selected client is invalid. Please choose a client from Client list.', 'Validation')
        setLoading(false)
        return
      }

      if (isConfirm && (!formData.po_no || !formData.po_no.trim())) {
        showMessage('PO No. is required when status is Confirm', 'Validation')
        setLoading(false)
        return
      }
      
      let createdProjectId = null
      let createdProject = null
      
      if (editingId) {
        // Update existing project
        let attachmentPath = formData.po_attachment_name || null
        if (isConfirm) {
          attachmentPath = await upsertPurchaseOrderForProject({
            projectId: editingId,
            poNumber: formData.po_no,
            poValue: formData.po_value,
            companyName: formData.company_name,
            remarks: 'Auto-created from Project Confirm',
            file: formData.po_attachment,
            remarksType: formData.po_type || 'Incoming',
            detailItems: items
          })
        }

        const dataToSubmit = {
          ...formData,
          client_id: selectedClient.client_id,
          company_name: selectedClient.client_name,
          po_attachment: isConfirm ? attachmentPath : (formData.po_attachment_name || null)
        }
        delete dataToSubmit.po_attachment_name
        delete dataToSubmit.po_type
        await projectService.update(editingId, dataToSubmit)

        if (isConfirm) {
          await syncQuotationForProject({
            projectId: editingId,
            status: 'Confirm',
            poNo: formData.po_no,
            poAttachment: attachmentPath,
            amount: formData.po_value
          })
        }
      } else {
        // Create new project with created_at timestamp
        const dataToSubmit = {
          ...formData,
          client_id: selectedClient.client_id,
          company_name: selectedClient.client_name,
          status: isConfirm ? 'Proposal' : formData.status,
          po_attachment: null
        }
        
        // Auto-generate project code if it's empty
        if (!dataToSubmit.project_code) {
          dataToSubmit.project_code = generateProjectNumber()
        }

        delete dataToSubmit.po_attachment_name
        delete dataToSubmit.po_attachment
        delete dataToSubmit.po_type

        const newProject = {
          ...dataToSubmit,
          created_at: new Date().toISOString()
        }
        const response = await projectService.create(newProject)
        createdProject = response.data.data
        createdProjectId = response.data.data?.project_id || response.data.project_id
        
        // Save detail items for newly created project
        if (createdProjectId && items.length > 0) {
          for (const item of items) {
            await projectService.addDetailItem(createdProjectId, {
              description: item.description,
              price: item.price,
              quantity: item.quantity
            })
          }
        }

        // If creating as Confirm, PO No and attachment are required and PO record must be created.
        if (isConfirm && createdProjectId) {
          const attachmentPath = await upsertPurchaseOrderForProject({
            projectId: createdProjectId,
            poNumber: formData.po_no,
            poValue: formData.po_value,
            companyName: formData.company_name,
            remarks: 'Auto-created from New Project (Confirm)',
            file: formData.po_attachment,
            remarksType: formData.po_type || 'Incoming',
            detailItems: items
          })

          await projectService.update(createdProjectId, {
            po_no: formData.po_no,
            po_attachment: attachmentPath,
            status: 'Confirm',
            confirm_date: new Date().toISOString()
          })

          await syncQuotationForProject({
            projectId: createdProjectId,
            status: 'Confirm',
            poNo: formData.po_no,
            poAttachment: attachmentPath,
            amount: formData.po_value
          })
        }
      }
      
      setShowModal(false)
      setEditingId(null)
      setIsViewMode(false)
      setFormData({
        project_code: '',
        client_id: '',
        po_no: '',
        po_attachment: null,
        po_attachment_name: '',
        project_name: '',
        company_name: '',
        po_value: '',
        status: 'Proposal',
        po_type: 'Incoming',
        quotation_generated: false
      })
      setItems([])

      showMessage('Project saved successfully!', 'Success')
      await fetchProjects()
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to save project:', error)
      showMessage(error.response?.data?.message || 'Failed to save project', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const getNextStatus = (currentStatus) => {
    const workflow = {
      'Proposal': 'Confirm',
      'Confirm': 'Ongoing',
      'Ongoing': 'Close',
      'Close': 'Close'
    }
    return workflow[currentStatus] || currentStatus
  }

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      setLoading(true)
      const project = projects.find(p => p.project_id === projectId)
      await projectService.update(projectId, {
        ...project,
        status: newStatus
      })
      await fetchProjects()
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to update status:', error)
      showMessage('Failed to update status: ' + (error.response?.data?.message || error.message), 'Error')
    } finally {
      setLoading(false)
    }
  }

  const getActionButtons = (project) => {
    if (isViewer) {
      return <span className="text-xs text-gray-400">View only</span>
    }

    switch (project.status) {
      case 'Proposal':
        // If quotation not yet generated, show Quotation button
        if (!project.quotation_generated) {
          return (
            <button
              onClick={() => handleQuotationClick(project)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
            >
              💡 Quotation
            </button>
          )
        } else {
          // After quotation generated, show Confirm button
          return (
            <div className="space-x-1 flex justify-center">
              <button
                onClick={() => handleConfirmClick(project)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
              >
                🤝 Confirm
              </button>
              <button
                onClick={() => handleCloseClick(project)}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
              >
                ✓ Close
              </button>
            </div>
          )
        }
      case 'Confirm':
        return (
          <div className="space-x-1 flex justify-center">
            <button
              onClick={() => handleAddPOClick(project)}
              className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
            >
              📋 New PO
            </button>
            <button
              onClick={() => handleInvoiceClick(project)}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
            >
              📄 Invoice
            </button>
            {/* <button
              onClick={() => handleCloseClick(project)}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
            >
              ✓ Close
            </button> */}
          </div>
        )
      case 'Ongoing':
        return (
          <div className="space-x-1 flex justify-center">
            <button
              onClick={() => handleAddPOClick(project)}
              className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
            >
               📋 New PO
            </button>
            <button
              onClick={() => handleInvoiceClick(project)}
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
            >
              📄 Invoice
            </button>
            {/* <button
              onClick={() => handleCloseClick(project)}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
            >
              ✓ Close
            </button> */}
          </div>
        )
      case 'Close':
        return (
          <button
            onClick={() => handleStatusChange(project.project_id, 'Ongoing')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-xs font-medium transition whitespace-nowrap"
          >
            🔄 Reopen
          </button>
        )
      default:
        return null
    }
  }

  const handleQuotationClick = (project) => {
    // Open quotation creation modal
    setSelectedProject(project)
    setQuotationFormData({
      quotation_amount: project.po_value || '',
      remarks: ''
    })
    setShowQuotationModal(true)
  }

  const handleCreateQuotation = async () => {
    try {
      if (!quotationFormData.quotation_amount) {
        showMessage('Please enter quotation amount', 'Validation')
        return
      }

      setLoading(true)

      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const random = Math.floor(Math.random() * 10000).toString().padStart(5, '0')
      const quotationNumber = `QT-${year}-${month}-${random}`

      // Create quotation
      const quotationResponse = await quotationService.create({
        project_id: selectedProject.project_id,
        quotation_number: quotationNumber,
        quotation_date: new Date().toISOString(),
        quotation_amount: parseFloat(quotationFormData.quotation_amount),
        status: 'Proposal',
        remarks: quotationFormData.remarks,
        remarks_type: 'Outgoing'
      })

      const createdQuotationId = quotationResponse?.data?.data?.quotation_id || quotationResponse?.data?.data?.id
      if (createdQuotationId) {
        try {
          await quotationService.generatePDF(createdQuotationId)
        } catch (pdfError) {
          // Quotation PDF refresh request failed
        }
      }

      // Mark quotation_generated as true for the project
      await projectService.update(selectedProject.project_id, {
        ...selectedProject,
        quotation_generated: true
      })

      showMessage(`Quotation ${quotationNumber} created successfully!\n\nYou can now click the Confirm button to proceed.`, 'Success')
      setShowQuotationModal(false)
      setSelectedProject(null)
      setQuotationFormData({
        quotation_amount: '',
        remarks: ''
      })
      await fetchProjects()
    } catch (error) {
      console.error('Failed to create quotation:', error)
      showMessage(error.response?.data?.message || 'Failed to create quotation', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleConfirmClick = (project) => {
    setPoModalMode('confirm')
    setSelectedProject(project)
    loadProjectDetailItems(project.project_id)
    setPoFormData({
      po_no: project.po_no || generatePONumber(),
      po_attachment: null,
      po_attachment_name: project.po_attachment || DEFAULT_PO_ATTACHMENT_URL
    })
    setShowPOModal(true)
  }

  const handleAddPOClick = (project) => {
    setSelectedProject(project)
    setPoModalMode('create')
    loadProjectDetailItems(project.project_id)
    setPoFormData({
      po_no: '',
      po_attachment: null,
      po_attachment_name: ''
    })
    setShowPOModal(true)
  }

  const handleSubmitPO = async () => {
    try {
      // Validation
      if (!poFormData.po_no) {
        showMessage('Please enter PO No.', 'Validation')
        return
      }

      setLoading(true)

      if (poModalMode === 'create') {
        const poDate = new Date().toISOString().split('T')[0]
        const detailItems = await buildPurchaseOrderDetailItems({ preferredItems: items })
        if (detailItems.length === 0) {
          showMessage('Please add at least one PO detail item before creating purchase order.', 'Validation')
          setLoading(false)
          return
        }
        if (poFormData.po_attachment) {
          const poForm = new FormData()
          poForm.append('project_id', selectedProject.project_id)
          poForm.append('po_number', poFormData.po_no)
          poForm.append('po_date', poDate)
          poForm.append('po_value', selectedProject.po_value || 0)
          poForm.append('vendor_client_details', selectedProject.company_name || '')
          poForm.append('remarks', 'Manual PO upload from Projects')
          poForm.append('remarks_type', 'Incoming')
          poForm.append('detail_items', JSON.stringify(detailItems))
          poForm.append('document', poFormData.po_attachment)

          await purchaseOrderService.createWithFile(poForm)
        } else {
          await purchaseOrderService.create({
            project_id: selectedProject.project_id,
            po_number: poFormData.po_no,
            po_date: poDate,
            po_value: selectedProject.po_value || 0,
            vendor_client_details: selectedProject.company_name || '',
            remarks: 'Manual PO upload from Projects',
            remarks_type: 'Incoming',
            status: 'Unpaid',
            detail_items: detailItems
          })
        }

        showMessage('Purchase order created successfully!', 'Success')
        await fetchProjectPurchaseOrders(selectedProject.project_id)
        setShowPOModal(false)
        setSelectedProject(null)
        setPoFormData({ po_no: '', po_attachment: null, po_attachment_name: '' })
        setPoModalMode('confirm')
        await fetchProjects()
        await fetchInvoices()
        return
      }

      const attachmentPath = await upsertPurchaseOrderForProject({
        projectId: selectedProject.project_id,
        poNumber: poFormData.po_no,
        poValue: selectedProject.po_value,
        companyName: selectedProject.company_name,
        remarks: 'Auto-created from Project Confirm',
        file: poFormData.po_attachment,
        remarksType: 'Incoming',
        detailItems: items
      })

      // Update project with PO No. and status to Confirm
      await projectService.update(selectedProject.project_id, {
        po_no: poFormData.po_no,
        po_attachment: attachmentPath,
        status: 'Confirm',
        confirm_date: new Date().toISOString()
      })

      await syncQuotationForProject({
        projectId: selectedProject.project_id,
        status: 'Confirm',
        poNo: poFormData.po_no,
        poAttachment: attachmentPath,
        amount: selectedProject.po_value
      })

      showMessage('Project confirmed and PO record created successfully!', 'Success')
      await fetchProjectPurchaseOrders(selectedProject.project_id)
      setShowPOModal(false)
      setSelectedProject(null)
      setPoFormData({ po_no: '', po_attachment: null, po_attachment_name: '' })
      setPoModalMode('confirm')
      await fetchProjects()
      await fetchInvoices()
    } catch (error) {
      console.error('Error confirming project:', error)
      showMessage('Failed to confirm project: ' + (error.response?.data?.message || error.message), 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateProjectField = async (projectId, field, value) => {
    try {
      setLoading(true)
      const project = projects.find(p => p.project_id === projectId)
      await projectService.update(projectId, {
        ...project,
        [field]: value
      })
      await fetchProjects()
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to update project:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteProject = async (project) => {
    const confirmed = await showConfirm(`Are you sure you want to delete project ${project.project_code || ''}? This action cannot be undone.`)
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await projectService.delete(project.project_id)
      showMessage('Project deleted successfully!', 'Success')
      await fetchProjects()
      await fetchInvoices()
    } catch (error) {
      console.error('Failed to delete project:', error)
      showMessage(error.response?.data?.message || 'Failed to delete project', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleCloseClick = async (project) => {
    const confirmed = await showConfirm('Are you sure you want to close this project? This action cannot be undone.')
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await projectService.update(project.project_id, {
        ...project,
        status: 'Close',
        close_date: new Date().toISOString()
      })
      await syncQuotationForProject({
        projectId: project.project_id,
        status: 'Close'
      })
      showMessage('Project closed successfully!', 'Success')
      await fetchProjects()
      await fetchInvoices()
    } catch (error) {
      console.error('Error closing project:', error)
      showMessage('Failed to close project: ' + (error.response?.data?.message || error.message), 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleInvoiceClick = async (project) => {
    let latestInvoices
    try {
      setLoading(true)
      const [invoiceResponse, creditNoteResponse] = await Promise.all([
        invoiceService.getAll(),
        creditNoteService.getAll()
      ])
      latestInvoices = invoiceResponse.data.data
      setInvoices(latestInvoices)
      setInvoiceModalCreditNotes(creditNoteResponse.data.data)
    } catch (error) {
      console.error('Failed to load invoice claim balances:', error)
      showMessage(error.response?.data?.message || 'Failed to load invoice claim balances. Please try again.', 'Error')
      return
    } finally {
      setLoading(false)
    }
    setSelectedProject(project)
    const purchaseOrders = await fetchProjectPurchaseOrders(project.project_id)

    if (purchaseOrders.length === 0) {
      showMessage('No purchase order found for this project. Please upload a PO first.', 'Validation')
      return
    }

    const initialPoId = purchaseOrders[0]?.po_id ? String(purchaseOrders[0].po_id) : ''
    setSelectedInvoicePoId(initialPoId)

    // Only force Partial when the selected PO already has partial invoices.
    if (hasPartialInvoices(project.project_id, initialPoId, latestInvoices, purchaseOrders)) {
      setPaymentType('Partial')
    } else {
      setPaymentType('Full')
    }
    setProgressPercentage(50)
    setClaimMode('percentage')
    setClaimAmountInput('')
    setShowInvoiceModal(true)
  }

  const generateInvoiceNumber = () => {
    // Generate invoice number format: INV-YYYY-MM-NNNNN
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const random = Math.floor(Math.random() * 10000).toString().padStart(5, '0')
    return `INV-${year}-${month}-${random}`
  }

  const hasPartialInvoices = (projectId, poId = null, invoiceRows = invoices, purchaseOrders = projectPurchaseOrders) => {
    const targetPoId = poId !== null && poId !== undefined && String(poId) !== '' ? String(poId) : null
    const targetPoNumber = targetPoId
      ? String(purchaseOrders.find((po) => String(po.po_id) === targetPoId)?.po_number || '').trim().toLowerCase()
      : ''

    const normalizeInvoiceType = (inv) => {
      const rawType = inv?.remarks_type || inv?.remarksType || inv?.inv_type || inv?.invType || inv?.invoice_type
      const normalized = String(rawType || '').trim().toLowerCase()
      if (normalized === 'outgoing') return 'Outgoing'
      if (normalized === 'incoming') return 'Incoming'
      return ''
    }

    return invoiceRows.some((inv) => {
      if (normalizeInvoiceType(inv) !== 'Outgoing') return false
      if (Number(inv?.project_id) !== Number(projectId)) return false

      if (targetPoId) {
        const samePoId = String(inv?.po_id || '') === targetPoId
        const invPoNumber = String(inv?.PurchaseOrder?.po_number || inv?.po_number_ref || inv?.po_number || '').trim().toLowerCase()
        const samePoNumber = targetPoNumber && invPoNumber && invPoNumber === targetPoNumber
        if (!samePoId && !samePoNumber) return false
      }

      const progress = Number.parseFloat(inv?.progress_claim_percentage || 0)
      return Number.isFinite(progress) && progress > 0 && progress < 100
    })
  }

  const handleInvoicePoChange = (poId) => {
    setSelectedInvoicePoId(poId)

    if (hasPartialInvoices(selectedProject?.project_id, poId)) {
      setPaymentType('Partial')
    } else {
      setPaymentType('Full')
    }
  }

  const getSelectedInvoicePoValue = () => {
    const selectedPo = projectPurchaseOrders.find((po) => String(po.po_id) === String(selectedInvoicePoId))
    return Number(selectedPo?.po_value || 0)
  }

  const getSelectedInvoicePoNumber = () => {
    const selectedPo = projectPurchaseOrders.find((po) => String(po.po_id) === String(selectedInvoicePoId))
    return String(selectedPo?.po_number || '').trim().toLowerCase()
  }

  // PO Status of the selected PO (Incoming / Outgoing). Defaults to Incoming.
  const getSelectedInvoicePoType = () => {
    const selectedPo = projectPurchaseOrders.find((po) => String(po.po_id) === String(selectedInvoicePoId))
    const rawType = String(selectedPo?.remarks_type ?? selectedPo?.remarksType ?? '').trim().toLowerCase()
    return rawType === 'outgoing' ? 'Outgoing' : 'Incoming'
  }

  // Invoice Type auto-assigns as the inverse of the PO Status:
  // Incoming PO (client pays us) → Outgoing invoice; Outgoing PO (we pay vendor) → Incoming invoice.
  const getInvoiceTypeForSelectedPo = () => (getSelectedInvoicePoType() === 'Incoming' ? 'Outgoing' : 'Incoming')

  const getPoClaimSummary = (projectId, poId = null) => {
    const po = projectPurchaseOrders.find((row) => String(row.po_id) === String(poId))
    return calculatePoClaims({
      invoices,
      creditNotes: invoiceModalCreditNotes,
      projectId,
      poId,
      poNumber: po?.po_number,
      poValue: po?.po_value
    })
  }

  const isProjectFullyClaimed = (projectId, poId = null) => getPoClaimSummary(projectId, poId).remainingClaimable <= 0

  // Net claimed amount is reduced by credit notes linked to invoices on this PO.
  const getClaimedAmountForPo = (projectId, poId = null) => getPoClaimSummary(projectId, poId).currentClaimed

  // Returns true when the Partial claim input (percentage or RM) is invalid / exceeds the 100% cap.
  const isPartialClaimInvalid = () => {
    if (paymentType !== 'Partial') return false

    const summary = getPoClaimSummary(selectedProject?.project_id, selectedInvoicePoId)
    const poValue = getSelectedInvoicePoValue() || 0

    if (claimMode === 'amount') {
      const enteredAmount = parseFloat(claimAmountInput)
      if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) return true
      return enteredAmount > summary.remainingClaimable + 0.005
    }

    return progressPercentage <= 0 || (poValue * progressPercentage) / 100 > summary.remainingClaimable + 0.005
  }

  const handleCreateInvoice = async (paymentOption) => {
    try {
      setLoading(true)

      const claimSummary = getPoClaimSummary(selectedProject.project_id, selectedInvoicePoId)

      if (claimSummary.remainingClaimable <= 0) {
        showMessage('This PO has no remaining claimable balance after current claims and credit notes. No additional invoice can be created.', 'Validation')
        setLoading(false)
        return
      }

      if (!selectedInvoicePoId) {
        showMessage('Please select PO No. before creating invoice.', 'Validation')
        setLoading(false)
        return
      }
      
      // Validate claim input for Partial payment (percentage or amount mode)
      if (paymentOption === 'Partial') {
        const selectedPoValue = getSelectedInvoicePoValue()
        const claimedAmount = getClaimedAmountForPo(selectedProject.project_id, selectedInvoicePoId)
        const remainingAmount = claimSummary.remainingClaimable

        if (claimMode === 'amount') {
          const enteredAmount = parseFloat(claimAmountInput)

          if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) {
            showMessage('Please enter a valid claim amount (RM) greater than 0.', 'Validation')
            setLoading(false)
            return
          }

          if (enteredAmount > remainingAmount + 0.005) {
            showMessage(`Claim amount exceeds the remaining claimable amount.\n\nPO Amount: RM ${selectedPoValue.toFixed(2)}\nAlready Claimed: RM ${claimedAmount.toFixed(2)}\nRemaining Claimable: RM ${remainingAmount.toFixed(2)}\nEntered: RM ${enteredAmount.toFixed(2)}\n\nMaximum claim is 100% of the PO amount.`, 'Validation')
            setLoading(false)
            return
          }
        } else {
          // Percentage mode
          if (progressPercentage <= 0 || progressPercentage > 100) {
            showMessage('Progress percentage must be greater than 0 and no more than 100', 'Validation')
            setLoading(false)
            return
          }

          if ((selectedPoValue * progressPercentage) / 100 > remainingAmount + 0.005) {
            showMessage(`Claim exceeds the remaining balance.\n\nRemaining Claimable: ${formatCurrency(remainingAmount)} (${claimSummary.remainingPercentage.toFixed(1)}%)\nNew Progress: ${progressPercentage}%\n\nPlease enter a lower progress percentage.`, 'Validation')
            setLoading(false)
            return
          }
        }
      }

      // Generate invoice number
      const invoiceNumber = generateInvoiceNumber()

      // Determine invoice status based on payment type
      let invoiceStatus = 'Generated'
      if (paymentOption === 'Full') {
        invoiceStatus = 'Pending'  // Full Payment: Status defaults to Pending (payment_amount empty at creation)
      } else if (paymentOption === 'Partial') {
        invoiceStatus = 'Pending'  // Partial Payment: Also Pending until payment uploaded
      }

      // Calculate progress percentage and amount
      let claimPercentage = 100
      const selectedPoValue = getSelectedInvoicePoValue()
      const selectedPoNumber = getSelectedInvoicePoNumber()
      let claimAmount = selectedPoValue

      if (paymentOption === 'Full') {
        claimAmount = claimSummary.remainingClaimable
        claimPercentage = selectedPoValue > 0 ? (claimAmount / selectedPoValue) * 100 : 0
      } else if (paymentOption === 'Partial') {
        if (claimMode === 'amount') {
          // Amount (RM) mode: derive percentage from the entered amount
          claimAmount = parseFloat(claimAmountInput)
          claimPercentage = selectedPoValue > 0 ? (claimAmount / selectedPoValue) * 100 : 0
        } else {
          // Percentage mode: derive amount from the entered percentage
          claimPercentage = progressPercentage
          claimAmount = (selectedPoValue * progressPercentage) / 100
        }
      }
      
      // Create invoice object
      const invoiceData = {
        invoice_number: invoiceNumber,
        project_id: selectedProject.project_id,
        po_id: parseInt(selectedInvoicePoId, 10),
        po_number: selectedPoNumber || undefined,
        invoice_date: new Date().toISOString(),
        invoice_amount: claimAmount,
        status: invoiceStatus,
        remarks_type: getInvoiceTypeForSelectedPo(),
        progress_claim_percentage: claimPercentage,
        progress_amount: claimAmount
      }
      
      // Create the invoice
      const invoiceResponse = await invoiceService.create(invoiceData)

      const createdInvoiceId = invoiceResponse?.data?.data?.invoice_id || invoiceResponse?.data?.data?.id
      if (createdInvoiceId) {
        try {
          await invoiceService.generatePDF(createdInvoiceId)
        } catch (pdfError) {
          // Invoice PDF refresh request failed
        }
      }
      
      // DO NOT automatically update project status
      // Wait for payment evidence upload to determine status change
      // For Full payment: Status will change to Close only after payment is uploaded
      // For Partial payment: Status will change to Ongoing after payment upload
      
      // Refresh invoices list
      await fetchInvoices()
      
      let statusMessage = ''
      if (paymentOption === 'Full') {
        statusMessage = '✓ Invoice created (Full Payment). Invoice Status: Pending.'
      } else {
        const currentStatus = selectedProject?.status
        if (currentStatus === 'Confirm') {
          statusMessage = '✓ Invoice created (Partial Payment). Invoice Status: Pending.'
        } else {
          statusMessage = '✓ Invoice created (Partial Payment). Invoice Status: Pending.'
        }
      }
      
      showMessage(`Invoice ${invoiceNumber} created successfully.\n\n${statusMessage}`, 'Success')
      setShowInvoiceModal(false)
      setSelectedProject(null)
      setProjectPurchaseOrders([])
      setSelectedInvoicePoId('')
      setPaymentType('Full')
      setProgressPercentage(50)
      setClaimMode('percentage')
      setClaimAmountInput('')
    } catch (error) {
      console.error('Failed to create invoice:', error)
      showMessage('Failed to create invoice: ' + (error.response?.data?.message || error.message), 'Error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">Projects</h1>
        {canMutate && (
          <button 
            onClick={openCreateModal}
            className="w-full rounded-lg bg-blue-600 px-4 py-2 font-medium text-white transition hover:bg-blue-700 sm:w-auto"
          >
            + New Project
          </button>
        )}
      </div>

      {/* {loading && <div className="text-center py-8 text-gray-600">Loading...</div>} */}

      {/* Filter Section */}
      <CollapsibleFilters>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Client Name Filter */}
          <div className="relative">
            <MultiSelectFilterDropdown
              label={`Client Name${filters.clientNames.length > 0 ? ` (${filters.clientNames.length})` : ''}`}
              options={getUniqueClientNames()}
              selectedValues={filters.clientNames}
              onChange={(clientNames) => setFilters(prev => ({ ...prev, clientNames }))}
              placeholder="All clients"
              emptyText="No project records"
            />
          </div>

          {/* PO Number Filter */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">PO No. {filters.poNumbers.length > 0 && <span className="text-blue-600 font-semibold">({filters.poNumbers.length})</span>}</label>
            <button
              type="button"
              onClick={() => toggleDropdown('poNumber')}
              className="w-full filter-trigger-standard border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <span className="truncate">{filters.poNumbers.length === 0 ? 'Select PO numbers...' : filters.poNumbers.join(', ')}</span>
              <span>{openDropdowns.poNumber ? '▲' : '▼'}</span>
            </button>
            {openDropdowns.poNumber && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                <input
                  type="text"
                  placeholder="Search..."
                  value={filterSearches.poNumber}
                  onChange={(e) => handleFilterSearchChange('poNumber', e.target.value)}
                  className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent sticky top-0 bg-white"
                />
                <div className="p-2 space-y-1">
                  {getUniquePONumbers().length > 0 ? (
                    getUniquePONumbers().map(code => (
                      <label key={code} className="flex items-center p-2 hover:bg-blue-50 cursor-pointer rounded">
                        <input
                          type="checkbox"
                          checked={filters.poNumbers.includes(code)}
                          onChange={(e) => handleFilterCheckChange('poNumbers', code, e.target.checked)}
                          className="mr-2 w-4 h-4 rounded"
                        />
                        <span className="text-sm font-mono font-semibold text-blue-600">{code}</span>
                      </label>
                    ))
                  ) : (
                    <div className="p-2 text-sm text-gray-500">No results</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Project Name Filter */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">Project Name {filters.projectNames.length > 0 && <span className="text-blue-600 font-semibold">({filters.projectNames.length})</span>}</label>
            <button
              type="button"
              onClick={() => toggleDropdown('projectName')}
              className="w-full filter-trigger-standard border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <span className="truncate">{filters.projectNames.length === 0 ? 'Select projects...' : filters.projectNames.join(', ')}</span>
              <span>{openDropdowns.projectName ? '▲' : '▼'}</span>
            </button>
            {openDropdowns.projectName && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                <input
                  type="text"
                  placeholder="Search..."
                  value={filterSearches.projectName}
                  onChange={(e) => handleFilterSearchChange('projectName', e.target.value)}
                  className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent sticky top-0 bg-white"
                />
                <div className="p-2 space-y-1">
                  {getUniqueProjectNames().length > 0 ? (
                    getUniqueProjectNames().map(name => (
                      <label key={name} className="flex items-center p-2 hover:bg-blue-50 cursor-pointer rounded">
                        <input
                          type="checkbox"
                          checked={filters.projectNames.includes(name)}
                          onChange={(e) => handleFilterCheckChange('projectNames', name, e.target.checked)}
                          className="mr-2 w-4 h-4 rounded"
                        />
                        <span className="text-sm">{name}</span>
                      </label>
                    ))
                  ) : (
                    <div className="p-2 text-sm text-gray-500">No results</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">Status {filters.statuses.length > 0 && <span className="text-blue-600 font-semibold">({filters.statuses.length})</span>}</label>
            <button
              type="button"
              onClick={() => toggleDropdown('status')}
              className="w-full filter-trigger-standard border border-gray-300 rounded-lg text-left text-sm flex items-center justify-between hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <span className="truncate">{filters.statuses.length === 0 ? 'Select status...' : filters.statuses.join(', ')}</span>
              <span>{openDropdowns.status ? '▲' : '▼'}</span>
            </button>
            {openDropdowns.status && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-20">
                <input
                  type="text"
                  placeholder="Search..."
                  value={filterSearches.status}
                  onChange={(e) => handleFilterSearchChange('status', e.target.value)}
                  className="w-full px-3 py-2 border-b border-gray-300 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent sticky top-0 bg-white"
                />
                <div className="p-2 space-y-1">
                  {getAllStatuses().map(status => (
                    <label key={status} className="flex items-center p-2 hover:bg-blue-50 cursor-pointer rounded">
                      <input
                        type="checkbox"
                        checked={filters.statuses.includes(status)}
                        onChange={(e) => handleFilterCheckChange('statuses', status, e.target.checked)}
                        className="mr-2 w-4 h-4 rounded"
                      />
                      <span className={`text-sm font-medium ${
                        status === 'Ongoing' ? 'text-yellow-700' :
                        status === 'Close' ? 'text-gray-700' :
                        status === 'Confirm' ? 'text-blue-700' :
                        'text-purple-700'
                      }`}>{status}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-end">
            <button
              onClick={clearAllFilters}
              className="btn-reset-standard"
            >
              🔄 Reset Filters
            </button>
          </div>
        </div>
      </CollapsibleFilters>

      <TableStateWrapper
        loading={loading}
        error={tableError}
        onRetry={fetchProjects}
        loadingText="Loading project records..."
        containerRef={projectTableRef}
      >
        <table className="w-full">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Project No.</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Project Name</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">PO Value</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Total Invoiced</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Outstanding</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Claim Progress</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Status</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Documentation</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Payment</th>
              {!isViewer && <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>}
            </tr>
          </thead>
          <tbody>
            {filteredProjects.length === 0 ? (
              <tr>
                <td colSpan={isViewer ? '9' : '10'} className="px-3 py-2 text-center text-gray-500">
                  No projects yet. Create your first project!
                </td>
              </tr>
            ) : (
              paginatedProjects.map((project) => (
                <tr 
                  key={project.project_id} 
                  onClick={() => openEditModal(project)}
                  className="border-b hover:bg-blue-50 cursor-pointer transition"
                >
                  <td className="px-3 py-2 text-sm font-semibold text-blue-600 whitespace-nowrap text-left">{project.project_code}</td>
                  <td className="px-3 py-2 text-sm font-medium text-gray-800 whitespace-nowrap text-left"><TruncatedText text={project.project_name} /></td>
                  <td className="px-3 py-2 text-sm font-semibold text-gray-800 whitespace-nowrap text-center">{formatCurrency(calculateProjectTotalPO(project))}</td>
                  <td className="px-3 py-2 text-sm font-semibold text-green-600 whitespace-nowrap text-center">{formatCurrency(calculateTotalInvoiced(project.project_id))}</td>
                  <td className="px-3 py-2 text-sm font-semibold text-orange-600 whitespace-nowrap text-center">{formatCurrency(calculateBalance(project.project_id))}</td>
                  <td className="px-3 py-2 text-sm font-semibold text-gray-800 whitespace-nowrap text-center">{(calculateProgress(project.project_id) * 1).toFixed(1)}</td>
                  <td className="px-3 py-2 text-sm  text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      project.status === 'Ongoing' ? 'bg-yellow-100 text-yellow-800' :
                      project.status === 'Close' ? 'bg-green-100 text-green-800' :
                      project.status === 'Confirm' ? 'bg-blue-100 text-blue-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {project.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-center" onClick={(e) => e.stopPropagation()}>
                    {getActionButtons(project)}
                  </td>
                  <td className="px-3 py-2 text-sm font-medium text-gray-700 whitespace-nowrap text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      getPaymentStatus(project) === 'Unpaid' ? 'bg-red-100 text-red-800' :
                      getPaymentStatus(project) === 'Paid' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {getPaymentStatus(project)}
                    </span>
                  </td>
                  {!isViewer && (
                    <td className="px-3 py-2 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openEditModal(project)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
        <TablePagination
          currentPage={projectPage}
          totalPages={totalProjectPages}
          totalItems={filteredProjects.length}
          rowsPerPage={projectRowsPerPage}
          onPrevious={() => setProjectPage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setProjectPage((prev) => Math.min(prev + 1, totalProjectPages))}
        />
      </TableStateWrapper>

      {/* Create/Edit Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="modal-panel-standard bg-white max-w-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold">
                {editingId ? `Edit Project No. ${formData.project_code}` : 'Add New Project'}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
                {/* Left Column */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PO No. {formData.status === 'Confirm' && <span className="text-red-600">*</span>}
                    </label>
                    <input
                      type="text"
                      name="po_no"
                      value={formData.po_no}
                      onChange={handleInputChange}
                      placeholder="eg. PO-2024-001"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      required={formData.status === 'Confirm'}
                      disabled={isViewMode || formData.status === 'Close'}
                    />
                    {formData.status === 'Confirm' && !formData.po_no && (
                      <p className="text-red-600 text-xs mt-1">PO No. is required for this status</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PO Status
                    </label>
                    <select
                      name="po_type"
                      value={formData.po_type}
                      onChange={(e) => {
                        const value = e.target.value
                        // Outgoing POs have no attachment — clear any selected file.
                        setFormData((prev) => value === 'Outgoing'
                          ? { ...prev, po_type: value, po_attachment: null, po_attachment_name: '' }
                          : { ...prev, po_type: value })
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      disabled={isViewMode || formData.status === 'Close'}
                    >
                      <option value="Incoming">Incoming</option>
                      <option value="Outgoing">Outgoing</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Project Name
                    </label>
                    <input
                      type="text"
                      name="project_name"
                      value={formData.project_name}
                      onChange={handleInputChange}
                      placeholder=" New Building"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      required
                      disabled={formData.status === 'Close'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PO Value (RM)
                    </label>
                    {editingId ? (
                      <div className="px-4 py-2 bg-blue-50 border border-blue-300 rounded-lg text-blue-700 font-semibold">
                        {formatAmount(projectPoTotal)}
                      </div>
                    ) : (
                      <input
                        type="number"
                        name="po_value"
                        value={formData.po_value}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                        disabled
                      />
                    )}
                  </div>
                  {editingId && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total Invoiced (RM)
                        </label>
                        <div className="px-4 py-2 bg-green-50 border border-green-300 rounded-lg text-green-700 font-semibold">
                          {formatAmount(calculateTotalInvoiced(editingId))}
                        </div>
                      </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {!editingId && formData.po_type !== 'Outgoing' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        PO Attachment
                      </label>
                      {formData.po_attachment_name && (() => {
                        const attachment = getAttachmentDisplay(formData.po_attachment_name)
                        return (
                          <p className={`text-xs mb-1 font-medium ${attachment.isDefaultFile ? 'text-gray-500' : 'text-green-700'}`}>
                            {attachment.isDefaultFile ? attachment.fileName : `✓ ${attachment.fileName}`}
                          </p>
                        )
                      })()}
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null
                          if (file && !isAllowedAttachmentFile(file)) {
                            showMessage('Invalid file type. Please upload PDF, JPG, PNG, DOC, DOCX, XLS, or XLSX files only.', 'Validation')
                            e.target.value = ''
                            return
                          }
                          setFormData((prev) => ({
                            ...prev,
                            po_attachment: file,
                            po_attachment_name: file ? file.name : prev.po_attachment_name || DEFAULT_PO_ATTACHMENT_URL
                          }))
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                        disabled={isViewMode || formData.status === 'Close'}
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Name
                    </label>
                    <select
                      name="client_id"
                      value={formData.client_id}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      required
                      disabled={formData.status === 'Close'}
                    >
                      <option value="">Select Client</option>
                      {clients.map((client) => (
                        <option key={client.client_id} value={client.client_id}>
                          {client.client_no} - {client.client_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      disabled={formData.status === 'Close' || isViewMode || (editingId && formData.status !== 'Proposal')}
                    >
                      {editingId ? (
                        // For existing projects, show allowed statuses based on workflow
                        getAllowedStatuses(formData.status).map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))
                      ) : (
                        // For new projects, only show Proposal and Confirm
                        getNewProjectStatuses().map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))
                      )}
                    </select>
                  </div>

                  {editingId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Progress %
                      </label>
                      <div className="px-4 py-2 bg-blue-50 border border-blue-300 rounded-lg text-blue-700 font-semibold">
                        {(calculateProgress(editingId) * 1).toFixed(1)}%
                      </div>
                    </div>
                  )}
                  {editingId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Created Date
                      </label>
                      <div className="px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700">
                        {projects.find(p => p.project_id === editingId)?.created_at
                          ? formatDate(projects.find(p => p.project_id === editingId).created_at)
                          : '-'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Line Items Section */}
              {!editingId && (
                <div className="border-t pt-4 space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Detail Items</h3>
                  
                  {/* Line Items Table */}
                  <div ref={projectItemsTableRef} className="overflow-x-auto overflow-y-auto max-h-[70vh] border border-gray-300 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100 border-b border-gray-300">
                        <tr>
                          <th className="px-2 py-1 text-center font-semibold text-gray-700 w-8 whitespace-nowrap">#</th>
                          <th className="px-2 py-1 text-left font-semibold text-gray-700 whitespace-nowrap">Description</th>
                          <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Price</th>
                          <th className="px-2 py-1 text-center font-semibold text-gray-700 w-20 whitespace-nowrap">Qty</th>
                          <th className="px-2 py-1 text-right font-semibold text-gray-700 w-24 whitespace-nowrap">Amount</th>
                          {!isViewer && <th className="px-2 py-1 text-center font-semibold text-gray-700 w-24 whitespace-nowrap">Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedProjectItems.map((item, idx) => (
                          <tr key={item.id} className="border-b border-gray-200 hover:bg-gray-50">
                            <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-gray-50 whitespace-nowrap">{(projectItemsPage - 1) * projectItemsRowsPerPage + idx + 1}</td>
                            <td className="px-2 py-1 whitespace-nowrap">{item.description}</td>
                            <td className="px-2 py-1 text-right whitespace-nowrap">{formatCurrency(item.price)}</td>
                            <td className="px-2 py-1 text-center whitespace-nowrap">{item.quantity.toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">{formatCurrency(item.amount)}</td>
                            {!isViewer && (
                              <td className="px-2 py-1 text-center space-x-2">
                                <button
                                  type="button"
                                  onClick={() => startEditItem(item)}
                                  disabled={isViewMode || formData.status === 'Close'}
                                  title="Edit item"
                                  className="inline-flex items-center justify-center w-8 h-8 text-blue-500 hover:text-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  ✏️
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeItem(item.id)}
                                  disabled={isViewMode || formData.status === 'Close'}
                                  title="Delete item"
                                  className="inline-flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  ❌
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                        {editingItemId === null && !isViewer && !isViewMode && formData.status !== 'Close' && (
                          <tr className="border-b border-gray-200 bg-blue-50">
                            <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-blue-100 whitespace-nowrap">{items.length + 1}</td>
                            <td className="px-2 py-1">
                                  <textarea
                                    name="description"
                                    value={currentItem.description}
                                    onChange={handleItemChange}
                                    placeholder="Item description"
                                    rows="2"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
                                  />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                name="price"
                                value={currentItem.price}
                                onChange={handleItemChange}
                                placeholder="0.00"
                                step="0.01"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                name="quantity"
                                value={currentItem.quantity}
                                onChange={handleItemChange}
                                placeholder="0"
                                step="0.01"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                              />
                            </td>
                            <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                              {currentItem.price && currentItem.quantity
                                ? formatCurrency(parseFloat(currentItem.price) * parseFloat(currentItem.quantity))
                                : formatCurrency(0)}
                            </td>
                            <td className="px-2 py-1 text-center">
                              <button
                                type="button"
                                onClick={addItem}
                                title="Add new item"
                                className="inline-flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-700 transition"
                              >
                                ➕
                              </button>
                            </td>
                          </tr>
                        )}
                        {editingItemId !== null && (
                          <tr className="border-b border-gray-200 bg-green-50">
                            <td className="px-2 py-1 text-center font-semibold text-gray-600 bg-green-100 whitespace-nowrap">{items.findIndex(item => item.id === editingItemId) + 1}</td>
                            <td className="px-2 py-1">
                                  <textarea
                                    name="description"
                                    value={currentItem.description}
                                    onChange={handleItemChange}
                                    placeholder="Item description"
                                    rows="2"
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
                                  />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                name="price"
                                value={currentItem.price}
                                onChange={handleItemChange}
                                placeholder="0.00"
                                step="0.01"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-right"
                              />
                            </td>
                            <td className="px-2 py-1">
                              <input
                                type="number"
                                name="quantity"
                                value={currentItem.quantity}
                                onChange={handleItemChange}
                                placeholder="0"
                                step="0.01"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs text-center"
                              />
                            </td>
                            <td className="px-2 py-1 text-right font-semibold text-gray-900 whitespace-nowrap">
                              {currentItem.price && currentItem.quantity
                                ? formatCurrency(parseFloat(currentItem.price) * parseFloat(currentItem.quantity))
                                : formatCurrency(0)}
                            </td>
                            <td className="px-2 py-1 text-center space-x-2">
                              <button
                                type="button"
                                onClick={() => saveEditItem(editingItemId)}
                                title="Save changes"
                                className="inline-flex items-center justify-center w-8 h-8 bg-green-600 hover:bg-green-700 text-white rounded transition"
                              >
                                ✓
                              </button>
                              <button
                                type="button"
                                onClick={cancelEditItem}
                                title="Cancel editing"
                                className="inline-flex items-center justify-center w-8 h-8 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
                              >
                                ✕
                              </button>
                            </td>
                          </tr>
                        )}
                        {items.length > 0 && (
                          <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                            <td className="px-2 py-1"></td>
                            <td className="px-2 py-1 text-right whitespace-nowrap">Total:</td>
                            <td className="px-2 py-1"></td>
                            <td className="px-2 py-1"></td>
                            <td className="px-2 py-1 text-right text-blue-700 whitespace-nowrap">{formatCurrency(getTotalAmount())}</td>
                            <td></td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <TablePagination
                      currentPage={projectItemsPage}
                      totalPages={totalProjectItemPages}
                      totalItems={items.length}
                      rowsPerPage={projectItemsRowsPerPage}
                      onPrevious={() => setProjectItemsPage((prev) => Math.max(prev - 1, 1))}
                      onNext={() => setProjectItemsPage((prev) => Math.min(prev + 1, totalProjectItemPages))}
                    />
                  </div>
                </div>
              )}

              {/* Process Flow History Section */}
              {editingId && (
                <div className="border-t pt-6 space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">Process Flow</h3>
                  {(() => {
                    const flowArray = getProcessFlow()
                    return (
                  <div className="flex items-center justify-between w-full px-1 sm:px-2">
                    {flowArray.map((flow, index) => {
                      const isLast = index === flowArray.length - 1
                      const isCompletedOrCurrent = flow.isCompleted || flow.isCurrent
                      return (
                        <div key={flow.status} className="flex items-center min-w-0 flex-1">
                          {/* Step Circle */}
                          <div className="flex flex-col items-center min-w-0">
                            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-sm sm:text-base ${
                              flow.isSkipped
                                ? 'bg-red-500 text-white border-2 border-red-500'
                                : isCompletedOrCurrent
                                ? 'bg-green-500 text-white border-2 border-green-500' 
                                : 'bg-gray-200 text-gray-700 border-2 border-gray-300'
                            }`}>
                              {flow.isSkipped ? '✕' : isCompletedOrCurrent ? '✓' : ''}
                            </div>
                            <p className="mt-2 text-center text-[11px] sm:text-xs font-semibold text-gray-700 leading-tight w-14 sm:w-16 break-words">
                              {flow.status}
                            </p>
                            <p className="mt-1 text-center text-[10px] sm:text-xs text-gray-500 leading-tight w-14 sm:w-16 break-words">
                              {flow.displayText}
                            </p>
                          </div>
                          
                          {/* Connecting Line */}
                          {!isLast && (
                            <div className={`h-1 flex-1 min-w-[10px] mx-1 sm:mx-2 ${
                              flow.isCompleted ? 'bg-green-500' : 'bg-gray-300'
                            }`}></div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                    )
                  })()}
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="submit"
                    disabled={loading || !canMutate || formData.status === 'Close'}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition disabled:opacity-50"
                >
                  {loading ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-2">
                Click outside to close
              </p>
            </form>
          </div>
        </div>
      )}

      {/* Invoice Payment Type Modal */}
      {showInvoiceModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowInvoiceModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold">Create Invoice</h2>
              <button
                type="button"
                onClick={() => setShowInvoiceModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              Project: {selectedProject?.project_code} - {selectedProject?.project_name}
            </p>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  PO No.
                </label>
                <select
                  value={selectedInvoicePoId}
                  onChange={(e) => handleInvoicePoChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="">Select PO No.</option>
                  {projectPurchaseOrders.map((po) => (
                    <option key={po.po_id} value={po.po_id}>
                      {po.po_number}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Type
                </label>
                <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium flex items-center justify-between">
                  <span>{selectedInvoicePoId ? getInvoiceTypeForSelectedPo() : '-'}</span>
                  {selectedInvoicePoId && (
                    <span className="text-xs font-normal text-gray-500">
                      auto-assigned (PO Status: {getSelectedInvoicePoType()})
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Payment Type
                </label>
                <div className="space-y-2">
                  <label className={`flex items-center ${hasPartialInvoices(selectedProject?.project_id, selectedInvoicePoId) ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <input
                      type="radio"
                      name="paymentType"
                      value="Full"
                      checked={paymentType === 'Full'}
                      onChange={(e) => setPaymentType(e.target.value)}
                      disabled={hasPartialInvoices(selectedProject?.project_id, selectedInvoicePoId)}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-700">
                      Full Payment (Status → Close)
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="paymentType"
                      value="Partial"
                      checked={paymentType === 'Partial'}
                      onChange={(e) => setPaymentType(e.target.value)}
                      className="mr-3"
                    />
                    <span className="text-sm text-gray-700">
                      Partial Payment (Status → Ongoing)
                    </span>
                  </label>
                </div>
                
                {/* Info message if Full Payment is disabled */}
                {hasPartialInvoices(selectedProject?.project_id, selectedInvoicePoId) && (
                  <div className="mt-3 p-3 bg-blue-100 border border-blue-300 rounded text-sm text-blue-800">
                    ℹ️ <strong>Partial Payment Only:</strong> This PO has existing partial invoices, so the next invoice for this PO must remain partial.
                  </div>
                )}

                {isProjectFullyClaimed(selectedProject?.project_id, selectedInvoicePoId) && (
                  <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded text-sm text-red-800">
                    ⚠️ <strong>Invoice blocked:</strong> This PO has no remaining claimable balance after current claims and credit notes.
                  </div>
                )}
              </div>

              {/* Claim input - Only show for Partial payment */}
              {paymentType === 'Partial' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Claim By
                  </label>
                  {/* Toggle: Percentage vs Amount */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => setClaimMode('percentage')}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition ${
                        claimMode === 'percentage'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Percentage (%)
                    </button>
                    <button
                      type="button"
                      onClick={() => setClaimMode('amount')}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition ${
                        claimMode === 'amount'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      Amount (RM)
                    </button>
                  </div>

                  {(() => {
                    const claimSummary = getPoClaimSummary(selectedProject?.project_id, selectedInvoicePoId)
                    const poValue = getSelectedInvoicePoValue() || 0
                    const claimedAmount = claimSummary.currentClaimed
                    const remainingAmount = claimSummary.remainingClaimable
                    const remainingPercentage = claimSummary.remainingPercentage

                    // Derive the entered claim in both units regardless of mode
                    const enteredPercentage = claimMode === 'amount'
                      ? (poValue > 0 ? (parseFloat(claimAmountInput) || 0) / poValue * 100 : 0)
                      : progressPercentage
                    const enteredAmount = claimMode === 'amount'
                      ? (parseFloat(claimAmountInput) || 0)
                      : (poValue * progressPercentage) / 100

                    const isError = enteredAmount > remainingAmount + 0.005
                    const isFull = !isError && enteredAmount > 0 && Math.abs(remainingAmount - enteredAmount) <= 0.005

                    return (
                      <>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">Current Claimed</label>
                            <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium">
                              <span title="Remaining claimable percentage of the PO amount">{remainingPercentage.toFixed(1)}%</span>&nbsp;·&nbsp;{formatCurrency(claimedAmount)}
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-gray-600 mb-1 block">
                              {claimMode === 'amount' ? 'New Claim Amount (RM)' : 'New Progress (%)'}
                            </label>
                            {claimMode === 'amount' ? (
                              <input
                                type="number"
                                value={claimAmountInput}
                                onChange={(e) => setClaimAmountInput(e.target.value)}
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${isError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                              />
                            ) : (
                              <input
                                type="number"
                                value={progressPercentage}
                                onChange={(e) => setProgressPercentage(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                                min="0"
                                max="100"
                                step="0.01"
                                placeholder="0"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent ${isError ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                              />
                            )}
                          </div>
                        </div>

                        <p className="text-xs text-gray-500 mb-1">
                          Total invoiced for this PO: {formatCurrency(claimSummary.totalInvoiced)}
                          {' '}− Credit Notes: {formatCurrency(claimSummary.totalCreditNotes)}
                        </p>

                        {/* Live equivalent conversion */}
                        <p className="text-xs text-gray-500 mb-1">
                          {claimMode === 'amount'
                            ? `= ${enteredPercentage.toFixed(1)}% of ${formatCurrency(poValue)} PO amount`
                            : `= ${formatCurrency(enteredAmount)} of ${formatCurrency(poValue)} PO amount`
                          }
                        </p>

                        {/* Validation message */}
                        <div className={`mt-1 p-2 rounded ${
                          isError
                            ? 'bg-red-50 border border-red-200'
                            : isFull
                              ? 'bg-green-50 border border-green-200'
                              : 'bg-gray-50 border border-gray-200'
                        }`}>
                          {isError ? (
                            <p className="text-xs text-red-700 font-medium">
                              ⚠️ Exceeds balance: New claim {formatCurrency(enteredAmount)}. Maximum claimable: {formatCurrency(remainingAmount)} ({remainingPercentage.toFixed(1)}%)
                            </p>
                          ) : isFull ? (
                            <p className="text-xs text-green-700 font-medium">
                              ✓ Remaining balance fully claimed ({formatCurrency(remainingAmount)}) - Status will change to Close
                            </p>
                          ) : (
                            <p className="text-xs text-gray-600">
                              Balance: {formatCurrency(remainingAmount)} ({remainingPercentage.toFixed(1)}%)
                            </p>
                          )}
                        </div>
                      </>
                    )
                  })()}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowInvoiceModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleCreateInvoice(paymentType)}
                disabled={
                  !selectedInvoicePoId ||
                  isProjectFullyClaimed(selectedProject?.project_id, selectedInvoicePoId) ||
                  isPartialClaimInvalid()
                }
                className={`flex-1 px-4 py-2 text-white rounded-lg transition ${
                  !selectedInvoicePoId ||
                  isProjectFullyClaimed(selectedProject?.project_id, selectedInvoicePoId) ||
                  isPartialClaimInvalid()
                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quotation Creation Modal */}
      {showQuotationModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowQuotationModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-bold">Create Quotation</h2>
              <button
                type="button"
                onClick={() => setShowQuotationModal(false)}
                className="modal-close-button"
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              PO: {selectedProject?.project_code} - {selectedProject?.project_name}
            </p>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quotation Amount (RM) *
                </label>
                <input
                  type="number"
                  value={quotationFormData.quotation_amount}
                  onChange={(e) => setQuotationFormData({
                    ...quotationFormData,
                    quotation_amount: e.target.value
                  })}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Remarks
                </label>
                <textarea
                  value={quotationFormData.remarks}
                  onChange={(e) => setQuotationFormData({
                    ...quotationFormData,
                    remarks: e.target.value
                  })}
                  placeholder="Enter any remarks about this quotation..."
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleCreateQuotation}
                disabled={loading}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PO Confirmation Modal */}
      <ProjectPurchaseOrderCreateModal
        isOpen={showPOModal && poModalMode === 'create'}
        onClose={() => {
          setShowPOModal(false)
          setSelectedProject(null)
          setPoModalMode('confirm')
          setPoFormData({ po_no: '', po_attachment: null, po_attachment_name: '' })
        }}
        projects={projects}
        clients={clients}
        defaultProjectId={selectedProject?.project_id || ''}
        onCreated={async () => {
          if (selectedProject?.project_id) {
            await fetchProjectPurchaseOrders(selectedProject.project_id)
          }
          await fetchProjects()
          await fetchInvoices()
        }}
      />
      {modalElement}
    </div>
  )
}
